import { ApiError, apiPost } from '../api/client.js';
import { chunk, runWithConcurrency } from './bulk-upload.js';

/**
 * Bulk / folder upload — the byte-moving half.
 *
 * Files go up in groups: one POST mints signed URLs for the group right
 * before its PUTs (URLs expire after 1h, so none are minted far ahead), the
 * PUTs run 3 at a time straight to GCS, and one POST finalizes the group
 * (server validates + queues). Two POSTs per group keeps a few-hundred-file
 * folder well inside the 300-per-15-min global rate limit. Once a file is
 * finalized it is the server's job; the browser can go away.
 */

const GROUP_SIZE = 6;
const PUT_CONCURRENCY = 3;
const RATE_LIMIT_WAIT_MS = 60_000;
const MAX_RATE_LIMIT_RETRIES = 5;

export type BulkFileState = 'waiting' | 'uploading' | 'finalizing' | 'queued' | 'error';

export interface BulkUploadFile {
  file: File;
  filename: string;
  overwrite: boolean;
  md5Base64: string;
}

export interface BulkUploadCommon {
  ring: 'ring0' | 'ring1';
  classification?: 'public' | 'internal' | 'confidential' | 'restricted';
  accessGroups?: string[];
  confidentialKind?: 'internal' | 'vendor' | 'client';
  productKind?: 'oneview' | 'stactize' | 'surge';
}

export interface BulkUploadCallbacks {
  onFile: (
    index: number,
    state: BulkFileState,
    detail?: { pct?: number; message?: string },
  ) => void;
  isCancelled: () => boolean;
}

interface MintResult {
  filename: string;
  jobId?: string;
  uploadUrl?: string;
  error?: string;
  message?: string;
}

interface FinalizeResult {
  jobId: string;
  status?: string;
  error?: string;
  message?: string;
}

async function postWithRateLimitRetry<T>(path: string, body: unknown): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await apiPost<T>(path, body);
    } catch (err) {
      const limited = err instanceof ApiError && err.status === 429;
      if (!limited || attempt >= MAX_RATE_LIMIT_RETRIES) throw err;
      await new Promise((r) => setTimeout(r, RATE_LIMIT_WAIT_MS));
    }
  }
}

function putToSignedUrl(
  url: string,
  file: File,
  contentType: string,
  onPct: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onPct(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (HTTP ${xhr.status}).`));
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.send(file);
  });
}

export async function runBulkUpload(opts: {
  batchId: string;
  files: readonly BulkUploadFile[];
  common: BulkUploadCommon;
  contentTypeOf: (file: File) => string;
  callbacks: BulkUploadCallbacks;
}): Promise<void> {
  const { batchId, files, common, contentTypeOf, callbacks } = opts;
  const indexed = files.map((f, index) => ({ ...f, index }));

  for (const group of chunk(indexed, GROUP_SIZE)) {
    if (callbacks.isCancelled()) return;

    let minted: MintResult[];
    try {
      const res = await postWithRateLimitRetry<{ results: MintResult[] }>(
        '/api/v1/admin/knowledgebase/documents/upload-urls',
        {
          batchId,
          files: group.map((f) => ({
            filename: f.filename,
            contentType: contentTypeOf(f.file),
            sizeBytes: f.file.size,
            overwrite: f.overwrite,
            ring: common.ring,
            ...(common.classification ? { classification: common.classification } : {}),
            ...(common.accessGroups && common.accessGroups.length > 0
              ? { accessGroups: common.accessGroups }
              : {}),
            ...(common.confidentialKind ? { confidentialKind: common.confidentialKind } : {}),
            ...(common.productKind ? { productKind: common.productKind } : {}),
          })),
        },
      );
      minted = res.results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start the upload.';
      for (const f of group) callbacks.onFile(f.index, 'error', { message });
      continue;
    }

    const uploaded: Array<{ index: number; jobId: string; md5Base64: string }> = [];
    const work = group.map((f, i) => ({ f, mint: minted[i] }));
    await runWithConcurrency(work, PUT_CONCURRENCY, async ({ f, mint }) => {
      if (callbacks.isCancelled()) return;
      if (!mint?.jobId || !mint.uploadUrl) {
        callbacks.onFile(f.index, 'error', {
          message: mint?.message ?? 'The server refused this file.',
        });
        return;
      }
      callbacks.onFile(f.index, 'uploading', { pct: 0 });
      const put = (): Promise<void> =>
        putToSignedUrl(mint.uploadUrl!, f.file, contentTypeOf(f.file), (pct) =>
          callbacks.onFile(f.index, 'uploading', { pct }),
        );
      try {
        // One retry: transient network drops are the common failure on
        // long folder uploads, and the signed URL is still valid.
        await put().catch(() => put());
        uploaded.push({ index: f.index, jobId: mint.jobId, md5Base64: f.md5Base64 });
        callbacks.onFile(f.index, 'finalizing');
      } catch (err) {
        callbacks.onFile(f.index, 'error', {
          message: err instanceof Error ? err.message : 'Upload failed.',
        });
      }
    });

    if (uploaded.length === 0) continue;
    try {
      const res = await postWithRateLimitRetry<{ results: FinalizeResult[] }>(
        '/api/v1/admin/knowledgebase/documents/finalize-batch',
        { items: uploaded.map((u) => ({ jobId: u.jobId, md5Base64: u.md5Base64 })) },
      );
      const byJob = new Map(res.results.map((r) => [r.jobId, r]));
      for (const u of uploaded) {
        const r = byJob.get(u.jobId);
        if (r?.status) callbacks.onFile(u.index, 'queued');
        else
          callbacks.onFile(u.index, 'error', {
            message: r?.message ?? 'The server could not verify this upload.',
          });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not finalize the upload.';
      for (const u of uploaded) callbacks.onFile(u.index, 'error', { message });
    }
  }
}
